import { Box, SelectOption, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  EmployeesNewCompensationTaxonomiesDocument,
  EmployeesNewCompensationTaxonomiesQuery,
} from 'generated/resources/graphql'

import SelectField from 'modules/core/components/SelectField'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { isValidAmount } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import Compensation, {
  CUSTOM_TIME_CONDITION,
  UNITS_TYPE_UNITS,
} from 'modules/contracts/store/models/Compensation'
import {
  CalculationType,
  CompensationRateType,
  CompensationUnit,
  LegalEntityMeta,
  NO_POLICY_ID,
  TimeConditionType,
} from 'modules/employees/pages/BulkFixContractsPage/types'

import { FormSection } from '../_FormSection'
import {
  setAmountAction,
  setAmountTypeAction,
  setDescriptionAction,
  setMinimumAmountOfHoursAction,
  setPerWorkedDayCalculationAction,
  setRateTypeAction,
  setSyncWithPayrollAction,
  setTaxonomyAction,
  setTimeConditionAction,
} from '../actions'
import { NewCompensationStep, StepComponent, StepValidator } from '../types'

const MainDetailsStep: StepComponent = ({ data, onUpdate }) => {
  const {
    allowedLegalEntities,
    legalEntityId,
    contractsTaxonomyId,
    description,
    compensationType,
    calculation,
    timeCondition,
    minimumAmountOfHours,
    amount,
    unit,
    syncWithSupplements,
    policyId,
  } = data

  const { company, access } = useCurrentSession()

  /* ===== State helpers ====== */

  const { data: taxonomyData } = useQuery(EmployeesNewCompensationTaxonomiesDocument, {
    variables: {
      // eslint-disable-next-line no-type-assertion/no-type-assertion -- Legal entity must be provided to reach this step
      legalEntityId: legalEntityId!,
    },
  })

  const leCurrency = useUnsafeMemo(
    () => pluckLegalEntityCurrency(allowedLegalEntities, legalEntityId),
    [allowedLegalEntities, legalEntityId]
  )

  const taxonomyOptions = useUnsafeMemo(() => loadTaxonomyOptions(taxonomyData), [taxonomyData])
  const compensationRateTypeOptions = useLazyRef(loadCompensationTypeOptions)
  const timeConditionOptions = useLazyRef(loadTimeConditionOptions)
  const calculationOptions = useLazyRef(loadCalculationOptions)
  const unitOptions = useMemo(() => loadUnitOptions(leCurrency), [leCurrency])

  const isTaxonomySet = contractsTaxonomyId !== undefined
  const isPerWorkedDay = compensationType === Compensation.types.per_worked_day
  const isCustomTimeCondition = timeCondition === CUSTOM_TIME_CONDITION
  const amountDisabled = !compensationType || compensationType === Compensation.types.undefined

  /* ===== Event handlers ====== */

  const handleAmountChange = (enteredText: string): void => {
    let newValue = null
    /*
     * This logic is to avoid setting the amount to an invalid value
     * since the component is uncontrolled without a value
     */
    if (isValidAmount(enteredText)) {
      newValue = enteredText
    } else if (enteredText.length) {
      newValue = amount ? `${amount}` : null
    }

    /*
      Compensations assume cents when executing `Compensation.GetUnitValue`.
      This version assumes the number would be used entirely.
     */
    onUpdate(setAmountAction(newValue))
  }

  useEffect(() => {
    track(trackingEvents.fixAgreementInBulk.fixAgreementSelectPolicy, {
      companyId: company.id,
      acessId: access.id,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTaxonomyChange = useUnsafeCallback(
    (newTaxonomyId: string) => {
      if (!newTaxonomyId.length) {
        onUpdate(setTaxonomyAction(null))
        return
      }
      const selectedTaxonomy = taxonomyOptions.find((option) => option.value === newTaxonomyId)

      onUpdate(
        setTaxonomyAction(
          selectedTaxonomy
            ? {
                id: Number.parseInt(newTaxonomyId),
                name: selectedTaxonomy.label,
              }
            : null
        )
      )
    },
    [onUpdate, taxonomyOptions]
  )

  const handleCalculationChange = useUnsafeCallback(
    (newCalculation: string) => {
      if (isValidCalculation(newCalculation)) {
        onUpdate(setPerWorkedDayCalculationAction(newCalculation))
      }
    },
    [onUpdate]
  )

  const handleTimeConditionChange = useUnsafeCallback(
    (newTimeCondition: string) => {
      if (isValidTimeCondition(newTimeCondition)) {
        onUpdate(setTimeConditionAction(newTimeCondition))
      }
    },
    [onUpdate]
  )

  const handleMinHoursChange = useUnsafeCallback(
    (newMininumAmountOfHour: string) => {
      onUpdate(setMinimumAmountOfHoursAction(Number(newMininumAmountOfHour)))
    },
    [onUpdate]
  )

  const handleDescriptionChange = useUnsafeCallback(
    (newDescription: string) => {
      onUpdate(setDescriptionAction(newDescription))
    },
    [onUpdate]
  )

  const handleCompensationRateTypeChange = useUnsafeCallback(
    (newCompensationType: string) => {
      if (isValidCompensationType(newCompensationType)) {
        onUpdate(setRateTypeAction(newCompensationType))
      } else {
        onUpdate(setRateTypeAction(null))
      }
    },
    [onUpdate]
  )

  const handleUnitChange = useUnsafeCallback(
    (newUnit: string) => {
      if (isValidAmountType(newUnit, leCurrency)) {
        onUpdate(setAmountTypeAction(newUnit))
      }
    },
    [onUpdate, leCurrency]
  )

  const handleSyncWithPayrollChange = useUnsafeCallback(
    (value: boolean) => {
      onUpdate(setSyncWithPayrollAction(value))
    },
    [onUpdate]
  )

  return (
    <Box height="full" gap="s24">
      <FormSection>
        <Box gap="s24">
          <SelectField
            loading={!taxonomyData}
            label={i18n.t(
              'employees.bulk_edit.fix_conditions.steps.main_details.compensation_type_label'
            )}
            options={taxonomyOptions}
            none={i18n.t('common.select')}
            value={isTaxonomySet ? `${contractsTaxonomyId}` : undefined}
            onChange={handleTaxonomyChange}
            mandatory
          />
          <TextArea
            label={i18n.t(
              'employees.bulk_edit.fix_conditions.steps.main_details.description_label'
            )}
            value={description}
            onChange={handleDescriptionChange}
          />
          <SelectField
            mandatory
            label={i18n.t(
              'employees.bulk_edit.fix_conditions.steps.main_details.compensation_amount_type_label'
            )}
            options={compensationRateTypeOptions.current}
            none={i18n.t('common.select')}
            value={compensationType}
            onChange={handleCompensationRateTypeChange}
          />
          {isPerWorkedDay && (
            <>
              <SelectField
                label={i18n.t(
                  'contract_versions.forms.additional_compensation.modal.calculation_field'
                )}
                options={calculationOptions.current}
                value={calculation}
                onChange={handleCalculationChange}
              />
              <SelectField
                attachmentPosition="bottom right"
                label={i18n.t(
                  'contract_versions.forms.additional_compensation.modal.time_condition'
                )}
                options={timeConditionOptions.current}
                value={timeCondition}
                none={i18n.t('common.none')}
                onChange={handleTimeConditionChange}
              />
              {isCustomTimeCondition && (
                <TextField
                  label={i18n.t('contract_versions.forms.additional_compensation.modal.custom')}
                  value={minimumAmountOfHours ? `${minimumAmountOfHours}` : ''}
                  onChange={handleMinHoursChange}
                />
              )}
            </>
          )}
          <Box gap="s16" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            {/* This should be a NumberField but that doesn't (yet) support negative numbers. */}
            <TextField
              type="number"
              mandatory={!amountDisabled}
              label={amountFieldLabel(compensationType)}
              value={amount}
              readonly={amountDisabled}
              onChange={handleAmountChange}
            />
            <SelectField
              mandatory
              label={i18n.t(
                'employees.bulk_edit.fix_conditions.steps.main_details.compensation_unit_label'
              )}
              options={unitOptions}
              value={unit}
              onChange={handleUnitChange}
            />
          </Box>
        </Box>
      </FormSection>
      {/* Only show if the selected policyId is not the NO_POLICY option */}
      {policyId != NO_POLICY_ID && (
        <TogglerCheckbox
          label={i18n.t(
            'employees.bulk_edit.fix_conditions.steps.main_details.sync_with_payroll_label'
          )}
          value={!!syncWithSupplements}
          onChange={handleSyncWithPayrollChange}
        />
      )}
    </Box>
  )
}

/* ===== Copies ====== */

function amountFieldLabel(compensationType?: string): string {
  if (compensationType === Compensation.types.per_worked_day) {
    return i18n.t(
      'contract_versions.forms.additional_compensation.modal.amount_per_worked_day_field'
    )
  }

  return i18n.t('contract_versions.forms.additional_compensation.modal.amount_field')
}

function pluckLegalEntityCurrency(
  allowedLegalEntities: LegalEntityMeta[],
  legalEntityId: number | undefined
) {
  if (!legalEntityId) {
    return null
  }
  return allowedLegalEntities.find((le) => le.id === legalEntityId)?.currency ?? null
}

/* ===== Select Options ===== */

function loadTaxonomyOptions(
  taxonomyData?: EmployeesNewCompensationTaxonomiesQuery
): SelectOption[] {
  return (
    taxonomyData?.contracts.taxonomiesConnection.nodes
      .filter((taxonomy) => !taxonomy.archived)
      .map((taxonomy) => ({
        label: taxonomy.name,
        value: `${taxonomy.id}`,
      })) ?? []
  )
}

function loadCalculationOptions(): SelectOption[] {
  return Object.entries(Compensation.CalculationTypes()).map(([key, value]) => ({
    label: value,
    value: key,
  }))
}

function loadCompensationTypeOptions(): SelectOption[] {
  return Object.entries(Compensation.Types())
    .map(([key, value]) => ({
      label: value,
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

const loadTimeConditionOptions = (): SelectOption[] => {
  const timeConditionType = Compensation.TimeConditionTypes()
  return Object.entries(timeConditionType).map(([key, label]) => ({
    label: label ?? '',
    value: key,
  }))
}

function loadUnitOptions(leCurrency: string | null): SelectOption[] {
  return [
    {
      value: leCurrency ?? '',
      label: leCurrency ?? '',
    },
    {
      // The static method returns a units property, type should be improved
      label: Compensation.Unit(undefined).units ?? '',
      value: UNITS_TYPE_UNITS,
    },
  ]
}

/* ===== Validators ===== */

function isValidCalculation(newCalculation: string): newCalculation is CalculationType {
  return (
    newCalculation === Compensation.calculations.current_period ||
    newCalculation === Compensation.calculations.previous_period
  )
}

function isValidTimeCondition(newTimeCondition: string): newTimeCondition is TimeConditionType {
  return Object.keys(Compensation.TimeConditionTypes()).includes(newTimeCondition)
}

function isValidCompensationType(
  newCompensationType: string
): newCompensationType is CompensationRateType {
  return Object.values(Compensation.types).includes(newCompensationType)
}

function isValidAmountType(
  newAmountType: string,
  allowedCurrency: string | null
): newAmountType is CompensationUnit {
  return newAmountType === UNITS_TYPE_UNITS || newAmountType === allowedCurrency
}

const isCompletedStep: StepValidator = ({
  contractsTaxonomyId,
  compensationType,
  amount,
  unit,
}) => {
  return (
    !!contractsTaxonomyId &&
    !!compensationType &&
    (!!amount || compensationType === Compensation.types.undefined) &&
    !!unit
  )
}

/* ===== Exports ===== */

const stepDefinition: NewCompensationStep = () => ({
  Component: MainDetailsStep,
  isCompleted: isCompletedStep,
  title: i18n.t('employees.bulk_edit.fix_conditions.steps.main_details.title'),
})

export default stepDefinition
